import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { getStockCombinations } from '../../services/StockRepository';
import IDOptionLabel, {
  getIDOptionLabel,
} from '../../../../shared/components/mbui/IDOptionLabel';
import RHFAutocomplete from '../../../../shared/components/react-hook-form/RHFAutocomplete';
import RHFDatePicker from '../../../../shared/components/react-hook-form/RHFDatePicker';
import RHFForm from '../../../../shared/components/react-hook-form/RHFForm';

type Combination = {
  marketId: number | null;
  marketName: string | null;
  destinationPortUnLocode: string | null;
  destinationPortName: string | null;
  departurePortUnLocode: string | null;
  departurePortName: string | null;
  portOperatorId: number | null;
  portOperatorName: string | null;
};

const formSchema = z.object({
  market: z.string().nullable().optional(),
  portOperator: z.string().nullable().optional(),
  departurePort: z.string().nullable().optional(),
  destinationPort: z.string().nullable().optional(),
  date: z
    .any()
    .refine((v) => !!v, { message: 'Date is required' })
    .refine(
      (v) =>
        !v ||
        dayjs(v).isSame(dayjs(), 'day') ||
        dayjs(v).isBefore(dayjs(), 'day'),
      {
        message: 'Date cannot be in the future',
      }
    ),
});

export type RHFStockRecalculationFormValues = z.infer<typeof formSchema>;

export type StockCombinationKey = {
  date: string;
  marketId: number;
  destinationPortUnLocode: string;
  departurePortUnLocode: string;
  portOperatorId: number;
};

const RHFStockRecalculationForm = forwardRef<
  HTMLFormElement,
  {
    onSubmit?: (payload: {
      keys: StockCombinationKey[];
      isZeroOutStaleStocks: boolean;
    }) => void;
  }
>(({ onSubmit: propsOnSubmit = () => undefined }, ref) => {
  const [combinations, setCombinations] = useState<Combination[]>([]);
  const [zeroOutStock, setZeroOutStock] = useState(false);

  const defaultValues: RHFStockRecalculationFormValues = useMemo(
    () => ({
      market: null,
      portOperator: null,
      departurePort: null,
      destinationPort: null,
      date: dayjs(),
    }),
    []
  );

  const methods = useForm({
    defaultValues,
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  });

  const { watch, setValue } = methods;
  const market = watch('market');
  const portOperator = watch('portOperator');
  const departurePort = watch('departurePort');
  const destinationPort = watch('destinationPort');

  useEffect(() => {
    getStockCombinations()
      .then((data: Combination[]) => setCombinations(data))
      .catch((err: any) => console.error('Error fetching combinations:', err));
  }, []);

  // For each field, options are derived from combinations filtered by all OTHER selected fields
  const marketOptions = useMemo(() => {
    const relevant = combinations.filter((c) => {
      if (portOperator && String(c.portOperatorId) !== portOperator)
        return false;
      if (departurePort && c.departurePortUnLocode !== departurePort)
        return false;
      if (destinationPort && c.destinationPortUnLocode !== destinationPort)
        return false;
      return true;
    });
    return relevant
      .sort((a, b) => (a.marketName ?? '').localeCompare(b.marketName ?? ''))
      .map((c) => ({ label: c.marketName ?? '', value: String(c.marketId) }))
      .filter(
        (opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx
      );
  }, [combinations, portOperator, departurePort, destinationPort]);

  const portOperatorOptions = useMemo(() => {
    const relevant = combinations.filter((c) => {
      if (market && String(c.marketId) !== market) return false;
      if (departurePort && c.departurePortUnLocode !== departurePort)
        return false;
      if (destinationPort && c.destinationPortUnLocode !== destinationPort)
        return false;
      return true;
    });
    return relevant
      .sort((a, b) =>
        (a.portOperatorName ?? '').localeCompare(b.portOperatorName ?? '')
      )
      .map((c) => ({
        label: c.portOperatorName ?? String(c.portOperatorId),
        value: String(c.portOperatorId),
      }))
      .filter(
        (opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx
      );
  }, [combinations, market, departurePort, destinationPort]);

  const departurePortOptions = useMemo(() => {
    const relevant = combinations.filter((c) => {
      if (market && String(c.marketId) !== market) return false;
      if (portOperator && String(c.portOperatorId) !== portOperator)
        return false;
      if (destinationPort && c.destinationPortUnLocode !== destinationPort)
        return false;
      return true;
    });
    return relevant
      .sort((a, b) =>
        (a.departurePortUnLocode ?? '').localeCompare(
          b.departurePortUnLocode ?? ''
        )
      )
      .map((c) => ({
        label: `${c.departurePortUnLocode}${c.departurePortName ? ' ' + c.departurePortName : ''}`,
        value: c.departurePortUnLocode,
      }))
      .filter(
        (opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx
      );
  }, [combinations, market, portOperator, destinationPort]);

  const destinationPortOptions = useMemo(() => {
    const relevant = combinations.filter((c) => {
      if (market && String(c.marketId) !== market) return false;
      if (portOperator && String(c.portOperatorId) !== portOperator)
        return false;
      if (departurePort && c.departurePortUnLocode !== departurePort)
        return false;
      return true;
    });
    return relevant
      .sort((a, b) =>
        (a.destinationPortUnLocode ?? '').localeCompare(
          b.destinationPortUnLocode ?? ''
        )
      )
      .map((c) => ({
        label: `${c.destinationPortUnLocode}${c.destinationPortName ? ' ' + c.destinationPortName : ''}`,
        value: c.destinationPortUnLocode,
      }))
      .filter(
        (opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx
      );
  }, [combinations, market, portOperator, departurePort]);

  // Reset each field if its current value is no longer in the available options
  useEffect(() => {
    if (market && !marketOptions.find((o) => o.value === market)) {
      setValue('market', null);
    }
  }, [marketOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      portOperator &&
      !portOperatorOptions.find((o) => o.value === portOperator)
    ) {
      setValue('portOperator', null);
    }
  }, [portOperatorOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset zeroOutStock when portOperator is cleared
  useEffect(() => {
    if (!portOperator) {
      setZeroOutStock(false);
    }
  }, [portOperator]);

  useEffect(() => {
    if (
      departurePort &&
      !departurePortOptions.find((o) => o.value === departurePort)
    ) {
      setValue('departurePort', null);
    }
  }, [departurePortOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      destinationPort &&
      !destinationPortOptions.find((o) => o.value === destinationPort)
    ) {
      setValue('destinationPort', null);
    }
  }, [destinationPortOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (data: RHFStockRecalculationFormValues) => {
    const dateStr = dayjs(data.date).format('YYYY-MM-DD');

    const filtered = combinations.filter((c) => {
      if (data.market && String(c.marketId) !== data.market) return false;
      if (data.portOperator && String(c.portOperatorId) !== data.portOperator)
        return false;
      if (data.departurePort && c.departurePortUnLocode !== data.departurePort)
        return false;
      if (
        data.destinationPort &&
        c.destinationPortUnLocode !== data.destinationPort
      )
        return false;
      return true;
    });

    const keys: StockCombinationKey[] = filtered.map((c) => ({
      date: dateStr,
      marketId: c.marketId as number,
      destinationPortUnLocode: c.destinationPortUnLocode,
      departurePortUnLocode: c.departurePortUnLocode,
      portOperatorId: c.portOperatorId as number,
    }));

    propsOnSubmit({ keys, isZeroOutStaleStocks: zeroOutStock });
  };

  return (
    <RHFForm {...methods} onSubmit={onSubmit} ref={ref}>
      <Stack direction='column' spacing={2}>
        {/* Market Details */}
        <Stack direction='column' spacing={2}>
          <Typography variant='h5'>Market Details</Typography>
          <FormGroup>
            <Stack direction='row' gap={2}>
              <RHFAutocomplete
                name='market'
                options={marketOptions}
                isOptionEqualToValue={(option: any, value: any) =>
                  option?.value === value?.value
                }
                getOptionLabel={getIDOptionLabel}
                renderOption={(props: any, option: any) => (
                  <li {...props} key={option.value}>
                    <IDOptionLabel id={option.value} label={option.label} />
                  </li>
                )}
                placeholder='Select Market'
                label='Market'
              />
              <RHFAutocomplete
                name='portOperator'
                options={portOperatorOptions}
                placeholder='Select Port Operator'
                label='Port Operator'
              />
            </Stack>
          </FormGroup>
        </Stack>

        {/* Stock Details */}
        <FormGroup>
          <Stack direction='column' gap={2}>
            <Typography variant='h5'>Stock Details</Typography>
            <Stack direction='row' gap={2} alignItems='center'>
              <FormControl sx={{ flex: 1 }}>
                <RHFDatePicker
                  name='date'
                  label='Date'
                  disableFuture
                  minDate={dayjs('2026-04-01')}
                />
              </FormControl>
              <Tooltip
                title={
                  !portOperator
                    ? 'Select a Port Operator to enable this option'
                    : 'Zero out stocks without actual inflow from selected date to 42 days after'
                }
                arrow
              >
                <span style={{ flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={zeroOutStock}
                        onChange={(e) => setZeroOutStock(e.target.checked)}
                        disabled={!portOperator}
                      />
                    }
                    label='Zero Out Stale Stock'
                  />
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </FormGroup>

        {/* Port Details */}
        <FormGroup>
          <Stack direction='column' gap={2}>
            <Typography variant='h5'>Port Details</Typography>
            <Stack direction='row' gap={2}>
              <RHFAutocomplete
                name='departurePort'
                options={departurePortOptions}
                placeholder='Select Departure Port'
                label='Departure Port'
              />
              <RHFAutocomplete
                name='destinationPort'
                options={destinationPortOptions}
                placeholder='Select Destination Port'
                label='Destination Port'
              />
            </Stack>
          </Stack>
        </FormGroup>

        <Button type='submit' style={{ display: 'none' }} aria-hidden='true' />
      </Stack>
    </RHFForm>
  );
});

export default RHFStockRecalculationForm;
